import { useEffect, useState, type KeyboardEvent } from 'react';
import type { ClientWithTasks, Task } from '../../types/models';
import {
  activeTasks,
  clientMeta,
  estTotalLabel,
  refCount,
  sortByDue,
} from '../../lib/selectors';
import { TaskCheckbox } from '../TaskCheckbox';
import { Avatar } from '../Avatar';
import { DuePill, StatusPill } from '../Pills';
import { BoltIcon, CloseIcon, CursorSpark, LinkIcon } from '../Icon';
import btn from '../../styles/buttons.module.css';
import styles from './ClientDrawer.module.css';

interface TaskGroup {
  name: string;
  dot: string;
  tasks: Task[];
}

interface ClientDrawerProps {
  client: ClientWithTasks;
  onClose: () => void;
  onToggleTask: (taskId: string) => void;
  onAddTask: (title: string) => void;
  onCursor: (title: string) => void;
  onSelectTask: (taskId: string) => void;
  onToggleLineup: (taskId: string) => void;
  onEdit: () => void;
}

export function ClientDrawer({
  client,
  onClose,
  onToggleTask,
  onAddTask,
  onCursor,
  onSelectTask,
  onToggleLineup,
  onEdit,
}: ClientDrawerProps) {
  const [showDone, setShowDone] = useState(false);

  useEffect(() => {
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const active = activeTasks(client);
  const done = client.tasks.filter((t) => t.done);
  const next = sortByDue(active)[0];

  const projectIds = new Set(client.projects.map((p) => p.id));
  const groups: TaskGroup[] = [];
  for (const project of client.projects) {
    const tasks = active.filter((t) => t.projectId === project.id);
    if (tasks.length) {
      groups.push({ name: project.name, dot: 'var(--color-primary)', tasks });
    }
  }
  // Tasks with no project — or a project that no longer exists — group as loose.
  const loose = active.filter((t) => !t.projectId || !projectIds.has(t.projectId));
  if (loose.length) {
    groups.push({ name: 'Loose tasks', dot: 'var(--color-text-muted)', tasks: loose });
  }

  const nextColor =
    next?.due === 'overdue'
      ? 'var(--due-overdue)'
      : next?.due === 'today'
        ? 'var(--color-primary)'
        : 'var(--color-text-primary)';

  const handleAdd = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      onAddTask(e.currentTarget.value);
      e.currentTarget.value = '';
    }
  };

  return (
    <>
      <button
        type="button"
        className={styles.overlay}
        onClick={onClose}
        aria-label="Close drawer"
      />
      <aside className={`${styles.drawer} sb-scroll`} role="dialog" aria-modal="true">
        <div className={styles.head}>
          <Avatar client={client} size={44} />
          <div className={styles.headText}>
            <div className={styles.name}>{client.name}</div>
            <div className={styles.meta}>{clientMeta(client)}</div>
          </div>
          <button type="button" className={styles.editBtn} onClick={onEdit}>
            Edit
          </button>
          <button type="button" className={styles.close} onClick={onClose} aria-label="Close">
            <CloseIcon size={16} />
          </button>
        </div>

        <div className={styles.stats}>
          <div className={styles.stat}>
            <div className={styles.statValue}>{active.length}</div>
            <div className={styles.statLabel}>OPEN</div>
          </div>
          <div className={styles.stat}>
            <div className={styles.statValue}>{estTotalLabel(active)}</div>
            <div className={styles.statLabel}>EST. LEFT</div>
          </div>
          <div className={styles.stat}>
            <div className={styles.statValue} style={{ color: nextColor }}>
              {next ? next.dueLabel : '—'}
            </div>
            <div className={styles.statLabel}>NEXT DUE</div>
          </div>
        </div>

        {(client.website || client.contactEmail || client.phone || client.notes) && (
          <div className={styles.info}>
            {client.website && (
              <a
                className={styles.infoLink}
                href={client.website}
                target="_blank"
                rel="noreferrer"
              >
                {client.website.replace(/^https?:\/\//, '')} ↗
              </a>
            )}
            {client.contactEmail && (
              <a className={styles.infoLink} href={`mailto:${client.contactEmail}`}>
                {client.contactEmail}
              </a>
            )}
            {client.phone && <span className={styles.infoText}>{client.phone}</span>}
            {client.notes && <p className={styles.infoNotes}>{client.notes}</p>}
          </div>
        )}

        <div className={styles.body}>
          {groups.map((group) => (
            <div key={group.name} className={styles.group}>
              <div className={styles.groupHead}>
                <span className={styles.groupDot} style={{ background: group.dot }} />
                <span className={styles.groupName}>{group.name}</span>
                <span className={styles.groupCount}>{group.tasks.length} OPEN</span>
              </div>
              <div className={styles.groupTasks}>
                {group.tasks.map((task) => (
                  <div key={task.id} className={styles.taskCard}>
                    <div className={styles.taskTop}>
                      <TaskCheckbox
                        done={task.done}
                        onToggle={() => onToggleTask(task.id)}
                        label={`Complete: ${task.title}`}
                      />
                      <button
                        type="button"
                        className={styles.taskBody}
                        onClick={() => onSelectTask(task.id)}
                      >
                        <div
                          className={`${styles.taskTitle} ${task.done ? styles.taskTitleDone : ''}`}
                        >
                          {task.title}
                        </div>
                        <div className={styles.metaRow}>
                          {task.dueLabel && <DuePill due={task.due} label={task.dueLabel} />}
                          <StatusPill status={task.status} done={task.done} />
                          {task.est && <span className={styles.metaText}>{task.est}</span>}
                          {refCount(task) > 0 && (
                            <span className={styles.linkChip}>
                              <LinkIcon size={12} />
                              {refCount(task)}
                            </span>
                          )}
                        </div>
                      </button>
                      <button
                        type="button"
                        className={`${styles.lineupBtn} ${task.inLineup ? styles.lineupOn : ''}`}
                        onClick={() => onToggleLineup(task.id)}
                        title={task.inLineup ? 'Remove from Lineup' : 'Add to Lineup'}
                        aria-label={task.inLineup ? 'Remove from Lineup' : 'Add to Lineup'}
                        aria-pressed={task.inLineup ?? false}
                      >
                        <BoltIcon size={13} />
                      </button>
                    </div>
                    {task.ai && (
                      <button
                        type="button"
                        className={`${btn.btn} ${btn.cta} ${styles.handBtn}`}
                        onClick={() => onCursor(task.title)}
                      >
                        <CursorSpark size={14} />
                        Hand to Cursor
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}

          {done.length > 0 && (
            <div className={styles.doneSection}>
              <button
                type="button"
                className={styles.doneToggle}
                onClick={() => setShowDone((s) => !s)}
              >
                <span>{showDone ? '▾' : '▸'}</span> Completed · {done.length}
              </button>
              {showDone && (
                <div className={styles.doneList}>
                  {done.map((task) => (
                    <div key={task.id} className={styles.doneRow}>
                      <TaskCheckbox
                        done={task.done}
                        onToggle={() => onToggleTask(task.id)}
                        label={`Reopen: ${task.title}`}
                      />
                      <div className={`${styles.taskTitle} ${styles.taskTitleDone}`}>
                        {task.title}
                      </div>
                      <span className={styles.undo}>Undo</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        <div className={styles.addBar}>
          <input
            type="text"
            onKeyDown={handleAdd}
            placeholder="+ add a task to this client…"
            className={styles.addInput}
            aria-label={`Add a task to ${client.name}`}
          />
        </div>
      </aside>
    </>
  );
}
